import React, { useEffect, useState } from 'react';
import { AppId } from '../../hooks/windowManager';
import styles from './Dock.module.css';
import mascotIdle from './mascot/idle.gif';
import mascotStill from './mascot/rest.png';
import mascotWave from './mascot/wave.gif';

interface DockProps {
    openApps: AppId[];
    compact: boolean;
    onOpen: (id: AppId) => void;
}

const TRAY_ID = 'dock-tray';

/**
 * 맥 스타일 독 컴포넌트
 * 모든 아이콘이 패널을 열고, 다른 사이트로는 패널 안의 링크로 나갑니다.
 * 구분선 왼쪽은 이 사이트의 패널, 오른쪽은 다른 사이트로 이어지는 패널입니다.
 * 좁은 화면에서는 오른쪽 아래 토글 위로 세로로 펼치고, 넘치면 세로로 스크롤합니다.
 */
export const Dock: React.FC<DockProps> = ({ openApps, compact, onOpen }) => {
    const [tray, setTray] = useState(false);
    const trayOpen = compact && tray;

    useEffect(() => {
        if (!trayOpen) return;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setTray(false);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [trayOpen]);

    const appButton = (id: AppId, label: string, className: string, children: React.ReactNode) => {
        const isOpen = openApps.includes(id);

        return (
            <button
                type="button"
                className={`${styles.dockIcon} ${className} ${isOpen ? styles.running : ''}`}
                onClick={() => {
                    onOpen(id);
                    setTray(false);
                }}
                aria-label={label}
                aria-pressed={isOpen}
                title={label}
            >
                <div className={styles.iconContent}>{children}</div>
                {isOpen && <div className={styles.runningIndicator} />}
            </button>
        );
    };

    return (
        <>
            {trayOpen && <div className={styles.scrim} aria-hidden="true" onClick={() => setTray(false)} />}
            <nav className={`${styles.dock} ${trayOpen ? styles.trayOpen : ''}`} aria-label="패널과 사이트">
            <div className={styles.dockBackground}>
                <div id={TRAY_ID} className={styles.dockIcons} inert={compact && !tray}>
                    {appButton('about', '소개', '', (
                        <>
                            <img className={`${styles.mascot} ${styles.mascotIdle}`} src={mascotIdle} alt="" />
                            <img className={`${styles.mascot} ${styles.mascotWave}`} src={mascotWave} alt="" />
                            <img className={`${styles.mascot} ${styles.mascotStill}`} src={mascotStill} alt="" />
                        </>
                    ))}
                    {appButton('clock', '시계', styles.clockIcon, (
                        <div className={styles.clockDisplay}>
                            <div className={styles.clockTime}>12:34</div>
                            <div className={styles.clockDate}>MON</div>
                        </div>
                    ))}
                    {appButton('archive', '프로젝트', '', (
                        <svg className={styles.folderSvg} viewBox="0 0 30 28" shapeRendering="crispEdges" aria-hidden="true">
                            <path className={styles.folderBack} d="M1 2H12V4H14V6H29V27H1z" />
                            <path className={styles.folderFront} d="M1 8H29V27H1z" />
                            <path className={styles.folderLabel} d="M5 13H25V27H5z" />
                            <path className={styles.folderLines} d="M8 17H22V18H8zM8 21H17V22H8z" />
                        </svg>
                    ))}
                    {appButton('works', '작업', styles.terminalIcon, (
                        <div className={styles.terminalWindow}>
                            <div className={styles.terminalLine}></div>
                            <div className={styles.terminalLine}></div>
                            <div className={styles.terminalLine}></div>
                        </div>
                    ))}
                    {appButton('notes', '메모', '', (
                        <svg className={styles.noteSvg} viewBox="0 0 28 30" shapeRendering="crispEdges" aria-hidden="true">
                            <path className={styles.noteSheet} d="M1 1H27V21L19 29H1z" />
                            <path className={styles.noteFold} d="M19 29V21H27V23H25V25H23V27H21V29z" />
                            <path className={styles.noteLines} d="M5 7H23V8H5zM5 11H23V12H5zM5 15H23V16H5zM5 19H15V20H5z" />
                        </svg>
                    ))}
                    {appButton('settings', '설정', styles.settingsIcon, (
                        <div className={styles.gearIcon}>
                            <div className={styles.gearOuter}></div>
                            <div className={styles.gearInner}></div>
                        </div>
                    ))}
                    {/* 외부 사이트 구분선 */}
                    <div className={styles.dockDivider} />
                    {appButton('lab', '실험실', styles.labIcon, (
                        <svg className={styles.flaskSvg} viewBox="0 0 24 30" aria-hidden="true">
                            <defs>
                                <clipPath id="flaskInner">
                                    <path d="M9.5 2 H14.5 V11 L20 25.5 Q21 28.5 18 28.5 H6 Q3 28.5 4 25.5 L9.5 11 Z" />
                                </clipPath>
                            </defs>
                            <path className={styles.flaskGlassFill} d="M9.5 2 H14.5 V11 L20 25.5 Q21 28.5 18 28.5 H6 Q3 28.5 4 25.5 L9.5 11 Z" />
                            <g clipPath="url(#flaskInner)">
                                <path className={styles.flaskLiquid} d="M-6 16 Q0 13 6 16 T18 16 T30 16 V31 H-6 Z" />
                            </g>
                            <path className={styles.flaskGlass} d="M9.5 2 H14.5 V11 L20 25.5 Q21 28.5 18 28.5 H6 Q3 28.5 4 25.5 L9.5 11 Z" />
                            <line className={styles.flaskRim} x1="8.3" y1="2" x2="15.7" y2="2" />
                        </svg>
                    ))}
                    {appButton('feed', '책장', styles.feedIcon, (
                        <div className={styles.news}>
                            <div className={styles.newsMast}></div>
                            <div className={styles.newsBody}>
                                <div className={styles.newsPhoto}></div>
                                <div className={styles.newsCol}>
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            </div>
                            <div className={styles.newsLine}></div>
                            <div className={`${styles.newsLine} ${styles.newsShort}`}></div>
                        </div>
                    ))}
                    {appButton('mov', '극장', styles.movIcon, (
                        <svg className={styles.screenSvg} viewBox="0 0 32 24" shapeRendering="crispEdges" aria-hidden="true">
                            <rect className={styles.screenFrame} x="0.5" y="0.5" width="31" height="23" />
                            <path className={styles.screenGlyphs} d="M4 4h2v1H4zM9 4h1v1H9zM14 4h3v1h-3zM21 4h1v1h-1zM25 4h3v1h-3zM5 8h1v1H5zM26 8h2v1h-2zM4 13h2v1H4zM26 13h1v1h-1zM4 18h4v1H4zM24 18h4v1h-4zM11 19h1v1h-1zM20 19h1v1h-1z" />
                            <path className={styles.screenBody} d="M12 8h8v1h1v8h1v2H10v-2h1V9h1z" />
                            <path className={styles.screenLens} d="M13 11h2v2h-2zM17 11h2v2h-2z" />
                        </svg>
                    ))}
                </div>
                {compact && (
                    <button
                        type="button"
                        className={`${styles.dockIcon} ${styles.trayToggle}`}
                        onClick={() => setTray((current) => !current)}
                        aria-label={trayOpen ? '독 접기' : '독 펼치기'}
                        aria-expanded={trayOpen}
                        aria-controls={TRAY_ID}
                    >
                        <svg className={styles.traySvg} viewBox="0 0 18 18" shapeRendering="crispEdges" aria-hidden="true">
                            <path d={trayOpen
                                ? 'M1 1h3v3H1zM14 1h3v3h-3zM4 4h3v3H4zM11 4h3v3h-3zM7 7h4v4H7zM4 11h3v3H4zM11 11h3v3h-3zM1 14h3v3H1zM14 14h3v3h-3z'
                                : 'M1 1h4v4H1zM7 1h4v4H7zM13 1h4v4h-4zM1 7h4v4H1zM7 7h4v4H7zM13 7h4v4h-4zM1 13h4v4H1zM7 13h4v4H7zM13 13h4v4h-4z'}
                            />
                        </svg>
                    </button>
                )}
            </div>
            </nav>
        </>
    );
};

export default Dock;
